// Richieste.tsx — le sfide arrivate dal link personale. Mentre l'app è aperta (e non si sta giocando) si
// controlla ogni app.richieste_controllo_secondi; la più recente compare in alto: Accetta, Rifiuta, Rifiuta e blocca.
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useAccount } from "../servizi/account.ts";
import { eNumero, leggiImpostazione } from "../servizi/impostazioni.ts";
import { salvaToken } from "../servizi/sfide.ts";
import { accettaRichiesta, leggiRichiesteEAmici, rifiutaRichiesta, type RichiestaSfida } from "../servizi/social.ts";
import { impostaRicevute } from "../servizi/amici.ts";
import { vai } from "../servizi/percorso.ts";
import { suono } from "../gioco/festa.ts";
import { IMP_APP, PERCORSO_SFIDA } from "../costanti.ts";

export function Richieste({ attive }: { attive: boolean }) {
  const { t } = useTranslation();
  const account = useAccount();
  const [elenco, setElenco] = useState<RichiestaSfida[]>([]);
  const [errore, setErrore] = useState(false);
  const [invio, setInvio] = useState(false);
  const viste = useRef(new Set<number>());
  // v033: per tutti quelli con l'account (anche senza nome utente): il controllo tiene anche il «pallino verde»
  // per gli amici e porta il numero delle richieste di amicizia.
  const registrato = account.tipo === "registrato";

  const controlla = useCallback(async () => {
    try {
      const { richieste: r, amiciRicevute } = await leggiRichiesteEAmici();
      impostaRicevute(amiciRicevute);
      // Un suono solo per le richieste nuove (non a ogni controllo).
      if (r.some((x) => !viste.current.has(x.id))) suono("conto");
      r.forEach((x) => viste.current.add(x.id));
      setElenco(r);
    } catch {
      // rete giù: si riprova al prossimo giro
    }
  }, []);

  useEffect(() => {
    if (!registrato || !attive) return;
    const ogni = Math.max(5, leggiImpostazione(IMP_APP.richiesteControlloSecondi, 15, eNumero)) * 1000;
    void controlla();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") void controlla();
    }, ogni);
    return () => clearInterval(id);
  }, [registrato, attive, controlla]);

  // Il messaggio «scaduta» resta qualche secondo anche se non ci sono altre richieste.
  useEffect(() => {
    if (!errore) return;
    const id = setTimeout(() => setErrore(false), 5000);
    return () => clearTimeout(id);
  }, [errore]);

  const prima = elenco[0];
  if (!registrato || !attive) return null;
  if (!prima)
    return errore ? (
      <aside className="richiesta-sfida" role="status">
        <p className="nota">{t("social.richiesta_scaduta")}</p>
      </aside>
    ) : null;

  const togli = (id: number) => setElenco((e) => e.filter((x) => x.id !== id));
  const accetta = async () => {
    setInvio(true);
    setErrore(false);
    try {
      const r = await accettaRichiesta(prima.id);
      salvaToken(r.codice, r.token);
      togli(prima.id);
      vai(`${PERCORSO_SFIDA}${r.codice}`);
    } catch {
      togli(prima.id); // scaduta o già chiusa: sparisce
      setErrore(true);
    } finally {
      setInvio(false);
    }
  };
  const rifiuta = async (blocca: boolean) => {
    setInvio(true);
    try {
      await rifiutaRichiesta(prima.id, blocca);
    } catch {
      // già chiusa: sparisce comunque
    } finally {
      togli(prima.id);
      setInvio(false);
    }
  };

  return (
    <aside className="richiesta-sfida" role="alertdialog" aria-labelledby="richiesta-titolo" data-richiesta={prima.id}>
      <p className="richiesta-titolo" id="richiesta-titolo">
        {t("social.richiesta_titolo", { nome: prima.nome })}
      </p>
      {elenco.length > 1 && <p className="nota">{t("social.richieste_altre", { n: elenco.length - 1 })}</p>}
      {errore && <p className="nota" role="status">{t("social.richiesta_scaduta")}</p>}
      <div className="azioni-riga">
        <button type="button" className="pulsante pulsante-primario" disabled={invio} onClick={() => void accetta()}>
          {t("social.richiesta_accetta")}
        </button>
        <button type="button" className="pulsante pulsante-secondario" disabled={invio} onClick={() => void rifiuta(false)}>
          {t("social.richiesta_rifiuta")}
        </button>
      </div>
      <button type="button" className="pulsante-link" disabled={invio} onClick={() => void rifiuta(true)}>
        {t("social.richiesta_blocca")}
      </button>
    </aside>
  );
}
